import { useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { CompetitionAcceptRulesButtonCompetitionFragment$key } from "./__generated__/CompetitionAcceptRulesButtonCompetitionFragment.graphql";
import { CompetitionAcceptRulesButtonMutation as CompetitionAcceptRulesButtonMutationType } from "./__generated__/CompetitionAcceptRulesButtonMutation.graphql";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "@/utils/toast";
import { MdCheckCircleOutline } from "react-icons/md";
import { Button } from "@/kit/ui/button";
import { ConfirmDialog } from "./ConfirmDialog";
import { relayErrorMessage } from "@/utils/relay";

const CompetitionAcceptRulesButtonCompetitionFragment = graphql`
  fragment CompetitionAcceptRulesButtonCompetitionFragment on Competition {
    id
    latestRule {
      id
      entityAgreement @ifAllowed {
        id
        createdAt
      }
    }
  }
`;

const CompetitionAcceptRulesButtonMutation = graphql`
  mutation CompetitionAcceptRulesButtonMutation($competition: ID!) {
    agreeToCompetitionRule(competition: $competition) {
      id
      createdAt
      competitionRule {
        competition {
          id
          latestRule {
            id
            entityAgreement {
              id
              createdAt
            }
          }
          entityRuleAgreements(first: 1, latest: true) {
            edges {
              cursor
            }
          }
        }
      }
    }
  }
`;

interface Props {
  competition: CompetitionAcceptRulesButtonCompetitionFragment$key;
}

export default function CompetitionAcceptRulesButton({
  competition: competitionFragment,
}: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const intl = useIntl();
  const competition = useFragment(
    CompetitionAcceptRulesButtonCompetitionFragment,
    competitionFragment,
  );
  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionAcceptRulesButtonMutationType>(
      CompetitionAcceptRulesButtonMutation,
    );

  const onConfirm = () => {
    if (isMutationInFlight) {
      return;
    }
    commitMutation({
      variables: {
        competition: competition.id,
      },
      onError(error) {
        toast.error(relayErrorMessage(error));
      },
      onCompleted() {
        setDialogOpen(false);
        toast.success(
          intl.formatMessage({
            defaultMessage: "Track rules accepted!",
          }),
        );
      },
    });
  };

  if (competition.latestRule.entityAgreement) {
    return (
      <Button>
        <MdCheckCircleOutline />
        <FormattedMessage defaultMessage="Accepted" />
      </Button>
    );
  }

  return (
    <ConfirmDialog
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      onConfirm={onConfirm}
      title={<FormattedMessage defaultMessage="Accept the Rules" />}
      buttonMessage={<FormattedMessage defaultMessage="I Accept" />}
      message={
        <FormattedMessage defaultMessage="By clicking on the button below, I accept the rules of the Competition" />
      }
    />
  );
}
